// src/pages/OrganizerPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizer } from "../contexts/OrganizerContext";
import {
	FiCalendar,
	FiPlusCircle,
	FiTag,
	FiTrendingUp,
	FiUser,
	FiSettings,
	FiSmartphone,
} from "react-icons/fi";

import DashboardLayout from "../components/Layout/DashboardLayout";
import CreateEvent from "../components/Organizer/CreateEvent";
import EditEvent from "../components/Organizer/EditEvent";
import EventList from "../components/Organizer/EventList";
import TicketManagement from "../components/Organizer/TicketManagement";
import SalesManagement from "../components/Organizer/SalesManagement";
import ProfileManagement from "../components/Organizer/ProfileManagement";
import SettingsPage from "../components/Organizer/SettingsPage";
import OrganizerCheckInPage from "../components/Organizer/CheckIn/CheckInPage";
import { usePageTitle } from "../hooks/usePageTitle";

const OrganizerPage: React.FC = () => {
	const {
		isAuthenticated,
		isAuthLoading,
		hasFetched: authHasFetched,
		logout,
	} = useAuth();
	const { isOrganizer, isOrganizerLoading, hasFetched } = useOrganizer();
	const navigate = useNavigate();
	const location = useLocation();
	const currentPath = location.pathname;
	const [redirectChecked, setRedirectChecked] = useState(false);

	// リダイレクト処理が実行中かどうかのフラグ
	const [isRedirecting, setIsRedirecting] = useState(false);

	// リダイレクトの判断は、認証と主催者情報の取得が完了してから実施する
	useEffect(() => {
		// 認証と主催者データ取得が完了し、まだリダイレクトチェックが行われておらず、リダイレクト中でもない場合のみチェック
		if (
			!isAuthLoading &&
			authHasFetched &&
			!isOrganizerLoading &&
			hasFetched &&
			!redirectChecked &&
			!isRedirecting
		) {
			// リダイレクトチェック完了を記録
			setRedirectChecked(true);

			if (!isAuthenticated) {
				setIsRedirecting(true);
				navigate("/organizer/register");
				return;
			}

			if (!isOrganizer) {
				setIsRedirecting(true);
				navigate("/organizer/register");
				return;
			}
		}
		}, [
			isAuthenticated,
			isAuthLoading,
			authHasFetched,
			isOrganizer,
			isOrganizerLoading,
			hasFetched,
			navigate,
			redirectChecked,
			isRedirecting,
		]);

	const menuItems = [
		{
			path: "",
			label: "イベント管理",
			icon: <FiCalendar className="h-5 w-5" />,
		},
		{
			path: "events/create",
			label: "新規イベント作成",
			icon: <FiPlusCircle className="h-5 w-5" />,
		},
		{
			path: "tickets",
			label: "チケット管理",
			icon: <FiTag className="h-5 w-5" />,
		},
		{
			path: "check-in",
			label: "入場QRコード読み取り",
			icon: <FiSmartphone className="h-5 w-5" />,
		},
		{
			path: "sales",
			label: "売上管理",
			icon: <FiTrendingUp className="h-5 w-5" />,
		},
		{
			path: "profile",
			label: "主催者プロフィール",
			icon: <FiUser className="h-5 w-5" />,
		},
		{
			path: "settings",
			label: "設定",
			icon: <FiSettings className="h-5 w-5" />,
		},
	];

	// 現在のパスから適切なタイトルとサブタイトルを取得
	const getPageInfo = () => {
		const path = currentPath
			.replace("/organizer/", "")
			.replace("/organizer", "");
		if (path === "" || path === "events") {
			return {
				title: "イベント管理",
				subtitle: "登録済みイベントの一覧と管理",
			};
		} else if (path === "events/create") {
			return {
				title: "新規イベント作成",
				subtitle: "新しいイベントを作成します",
			};
		} else if (path.startsWith("events/") && path.endsWith("/edit")) {
			return { title: "イベント編集", subtitle: "イベント情報を編集します" };
		} else if (path === "tickets" || path.includes("/tickets")) {
			return { title: "チケット管理", subtitle: "チケットの発行と管理" };
		} else if (path === "sales") {
			return { title: "売上管理", subtitle: "売上データの確認と分析" };
		} else if (path === "profile") {
			return {
				title: "主催者プロフィール",
				subtitle: "プロフィール情報の確認と編集",
			};
		} else if (path === "settings") {
			return { title: "設定", subtitle: "システム設定の管理" };
		}
		return { title: "主催者ダッシュボード", subtitle: "" };
	};

	const pageInfo = getPageInfo();
	usePageTitle(pageInfo.title || "主催者ダッシュボード");

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/");
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	// 認証情報または主催者情報の読み込み中はローディング表示
	if (isAuthLoading || isOrganizerLoading || !authHasFetched || !hasFetched) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto"></div>
					<p className="mt-4 text-gray-600 dark:text-gray-400">読み込み中...</p>
				</div>
			</div>
		);
	}

	// リダイレクト中ならリダイレクト表示
	if (isRedirecting) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<p className="text-gray-600 dark:text-gray-400">
						主催者登録ページへリダイレクト中...
					</p>
				</div>
			</div>
		);
	}

	// 主催者情報がない場合はリダイレクト中表示
	if (!isAuthenticated || !isOrganizer) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<p className="text-gray-600 dark:text-gray-400">
						主催者登録ページへリダイレクト中...
					</p>
				</div>
			</div>
		);
	}

	return (
		<DashboardLayout
			title={pageInfo.title}
			subtitle={pageInfo.subtitle}
			menuItems={menuItems}
			basePath="/organizer"
			appName="主催者管理"
			appDescription="イベント管理システム"
			logoIcon={<FiCalendar className="h-6 w-6" />}
			onLogout={handleLogout}
			showHeader={false}
		>
			<Routes>
				<Route path="" element={<EventList />} />
				<Route path="events" element={<EventList />} />
				<Route path="events/create" element={<CreateEvent />} />
				<Route path="events/:id/edit" element={<EditEvent />} />
				<Route path="events/:id/tickets" element={<TicketManagement />} />
				<Route path="tickets" element={<TicketManagement />} />
				<Route path="check-in" element={<OrganizerCheckInPage />} />
				<Route path="sales" element={<SalesManagement />} />
				<Route path="profile" element={<ProfileManagement />} />
				<Route path="settings" element={<SettingsPage />} />
			</Routes>
		</DashboardLayout>
	);
};

export default OrganizerPage;
